/**
 * SettingsPanel.jsx — schema-driven settings panel for a widget.
 *
 * Rendered by the window settings popover when ⚙ is open. Two-way binds
 * each field directly to wm.setSettings(widget.id, {...nextSettings}),
 * which routes through the existing debounced persistence (DB for users,
 * localStorage for anon).
 *
 * Issue #3 — windowed vs maximized layers: schema fields marked
 * `scopable: true` can hold per-layer overrides. The panel renders a
 * top tab strip (Both / Windowed / Maximized) when at least one
 * scopable field exists, and writes the field into the layer the user
 * is currently editing.
 */

import { For, Show, createSignal, createMemo, createEffect } from 'solid-js';
import wm from '../lib/wmStore.js';
import { getSchema, isFieldVisible } from '../widgets/settingsSchemas.js';
import { pickSetting, patchLayer } from '../lib/settingsLayers.js';
import { GLASS_RECOVERY_COPY } from '../lib/glassMode.js';
import { tipProps } from './MouseTooltip.jsx';
import './SettingsPanel.css';

// Universal (non-schema) appearance keys that should follow the active
// editing layer so per-view glass/opacity can be configured independently.
// Issue #341 — these were previously always written to root, leaking
// across the windowed/maximized boundary.
const LAYER_AWARE_UNIVERSAL_KEYS = new Set(['glass', 'glassOpacity', 'glassBorderless', 'glassDataOnly']);

// Field-visibility gate (#133): admin-only fields are hidden unless
// the gate matches. `adminOnly: 'spy'` means render only when the
// viewer is an admin currently impersonating someone (Spy Mode).
function fieldVisible(field, identity) {
  return isFieldVisible(field, identity);
}

function fieldConditionVisible(field, settings, maximized, fields) {
  const cond = field?.showWhen;
  if (!cond || !cond.key) return true;
  const picked = pickSetting(settings || {}, cond.key, !!maximized);
  const fallback = fields?.find(f => f.key === cond.key)?.default;
  const current = picked === undefined ? fallback : picked;
  if (Object.prototype.hasOwnProperty.call(cond, 'equals')) return current === cond.equals;
  if (Object.prototype.hasOwnProperty.call(cond, 'notEquals')) return current !== cond.notEquals;
  return !!current;
}

export default function SettingsPanel(props) {
  const rawSchema = () => getSchema(props.widget.widget_type) || [];
  const settings = () => props.widget.settings || {};
  const hasScopable = createMemo(() => rawSchema().some(f => fieldVisible(f, wm.state.identity) && f.scopable));
  const [layer, setLayer] = createSignal(
    hasScopable() ? (props.widget.maximized ? 'maximized' : 'windowed') : 'root',
  );
  const conditionMaximized = () => {
    if (layer() === 'maximized') return true;
    if (layer() === 'windowed') return false;
    return !!props.widget.maximized;
  };
  const schema = () => rawSchema()
    .filter(f => fieldVisible(f, wm.state.identity))
    .filter(f => fieldConditionVisible(f, settings(), conditionMaximized(), rawSchema()));
  const meta = () => wm.state.catalog.find(c => c.type === props.widget.widget_type) || {};
  // Issue #341 — sync the default editing layer to the window's actual
  // maximized state, but ONLY when that state (or the schema's scopable
  // set) changes. The previous code called setLayer() during render, which
  // re-ran on every settings write and reset the user's explicit layer tab
  // after each keystroke — so edits to the non-active layer appeared to
  // "leak" into the other layer. Now the tab stays put until the window is
  // actually maximized/restored.
  createEffect(() => {
    const max = props.widget.maximized;
    if (hasScopable()) setLayer(max ? 'maximized' : 'windowed');
  });

  function set(key, value) {
    const field = schema().find(f => f.key === key);
    // Scopable schema fields and universal appearance keys (glass,
    // glassOpacity) both route to the active editing layer so per-view
    // settings are independent. Non-scopable schema fields always write
    // to root because the schema has not opted them in to per-layer.
    const lyr = ((field?.scopable || LAYER_AWARE_UNIVERSAL_KEYS.has(key)) && hasScopable())
      ? layer()
      : 'root';
    wm.setSettings(props.widget.id, patchLayer(settings(), lyr, { [key]: value }));
  }

  // Read a universal (non-schema) appearance key from the active editing
  // layer, with fallback to root so the field shows the inherited value
  // rather than appearing falsely unset (issue #341).
  function universalLayerValue(key, defaultVal) {
    if (!hasScopable() || layer() === 'root') return settings()[key] ?? defaultVal;
    const lyrObj = settings()[layer()];
    const v = (lyrObj && Object.prototype.hasOwnProperty.call(lyrObj, key)) ? lyrObj[key] : undefined;
    return v !== undefined ? v : (settings()[key] ?? defaultVal);
  }

  function reset() {
    wm.setSettings(props.widget.id, {});
  }

  function clearLayer(lyr) {
    if (lyr === 'root') { reset(); return; }
    const next = { ...settings() };
    delete next[lyr];
    wm.setSettings(props.widget.id, next);
  }

  // Resolve the value to display in the input for a given field, taking
  // the active editing layer into account.
  function valueFor(field) {
    if (field.scopable && hasScopable() && layer() !== 'root') {
      const lyr = settings()[layer()];
      return lyr ? lyr[field.key] : undefined;
    }
    return settings()[field.key];
  }

  return (
    <div class="v2-settings-panel" onPointerDown={(e) => e.stopPropagation()}>
      <header>
        <span>⚙ Settings · <code class="v2-settings-type">{props.widget.widget_type}</code></span>
      </header>
      <Show when={meta().description}>
        <p class="v2-settings-desc">{meta().description}</p>
      </Show>

      {/* Issue #118 — universal per-window appearance toggle. Lives outside
          the schema-driven grid because it applies to every widget. */}
      <fieldset class="v2-settings-glass">
        <legend>Window appearance</legend>
        <label class="v2-settings-glass__row">
          <input type="checkbox"
            checked={!!universalLayerValue('glass', false)}
            onChange={(e) => set('glass', e.currentTarget.checked)} />
          <span>Glass mode</span>
          <span class="v2-settings-field__help">Translucent chrome with a backdrop blur. Tune the opacity below.</span>
        </label>
        <Show when={!!universalLayerValue('glass', false)}>
          <label class="v2-settings-glass__row">
            <span style={{ 'min-width': '80px' }}>Opacity</span>
            <input type="range" min="10" max="100" step="5"
              value={Number(universalLayerValue('glassOpacity', 60))}
              onInput={(e) => set('glassOpacity', Number(e.currentTarget.value))} />
            <span style={{ 'min-width': '36px', 'text-align': 'right' }}>
              {Number(universalLayerValue('glassOpacity', 60))}%
            </span>
          </label>
          <label class="v2-settings-glass__row">
            <input type="checkbox"
              checked={!!universalLayerValue('glassBorderless', false)}
              onChange={(e) => set('glassBorderless', e.currentTarget.checked)} />
            <span>Borderless glass</span>
            <span class="v2-settings-field__help">Hide the frame, title bar, resize grips, and window buttons. {GLASS_RECOVERY_COPY}</span>
          </label>
          <label class="v2-settings-glass__row">
            <input type="checkbox"
              checked={!!universalLayerValue('glassDataOnly', false)}
              onChange={(e) => set('glassDataOnly', e.currentTarget.checked)} />
            <span>Data-only view</span>
            <span class="v2-settings-field__help">Hide supported search bars, filters, and widget toolbars so configured data has the stage.</span>
          </label>
        </Show>
      </fieldset>
      <Show when={hasScopable()}>
        <div class="v2-settings-layers" role="tablist" aria-label="Display layer">
          <span class="v2-settings-layers__hint">
            Apply to:
          </span>
          <button class="v2-btn-sm" classList={{ 'is-active': layer() === 'root' }}
            onClick={() => setLayer('root')}
            {...tipProps({ content: "Edit values that apply in BOTH windowed and maximized states (the default for fields with no per-view override).", pin: false })}>Both</button>
          <button class="v2-btn-sm" classList={{ 'is-active': layer() === 'windowed' }}
            onClick={() => setLayer('windowed')}
            {...tipProps({ content: "Edit values that apply ONLY when this widget is in its normal windowed size. Overrides the 'Both' value.", pin: false })}>Windowed</button>
          <button class="v2-btn-sm" classList={{ 'is-active': layer() === 'maximized' }}
            onClick={() => setLayer('maximized')}
            {...tipProps({ content: "Edit values that apply ONLY when this widget is maximized to fill the screen. Overrides the 'Both' value.", pin: false })}>Maximized</button>
          <Show when={layer() !== 'root' && settings()[layer()]}>
            <button class="v2-btn-sm v2-settings-layers__clear"
              onClick={() => clearLayer(layer())}
              {...tipProps({ content: "Clear this layer's overrides; root values resume", pin: false })}>Clear layer</button>
          </Show>
        </div>
        <p class="v2-settings-layers__help">
          Per-view fields (marked <span class="v2-settings-field__pill v2-settings-field__pill--inline">per-view</span>) only save the value to the layer above. Other fields always save globally.
        </p>
      </Show>
      <Show when={schema().length} fallback={null}>
        <div class="v2-settings-grid">
          <For each={schema()}>{(field) => {
            // Non-scopable fields are *always* edited at the root layer
            // (they aren't part of the per-layer override system) and so
            // they should remain interactive regardless of which layer
            // tab is active. Locking them was issue #21 — Kiosk Tracker
            // and similar widgets default the layer tab to "windowed" /
            // "maximized" so the zone/guild text inputs were rendered
            // with `pointer-events:none` and were uneditable.
            const isLocked = false;
            return (
              <label class="v2-settings-field"
                classList={{
                  'v2-settings-field--mode': field.type === 'select' && (field.key === 'mode' || field.key === 'metric' || field.key === 'source' || field.key === 'groupBy'),
                  'is-disabled': isLocked,
                }}>
                <span class="v2-settings-field__label">
                  {field.label}
                  <Show when={field.scopable}>
                    <span class="v2-settings-field__pill" {...tipProps("Can hold a different value per view (windowed vs maximized). Use the layer toggle above to edit each one.")}>per-view</span>
                  </Show>
                </span>
                <Field field={field}
                  value={valueFor(field)}
                  effective={pickSetting(settings(), field.key, props.widget.maximized)}
                  disabled={isLocked}
                  onInput={(v) => set(field.key, v)} />
                <Show when={field.help}>
                  <span class="v2-settings-field__help">{field.help}</span>
                </Show>
                <Show when={field.scopable && hasScopable() && layer() === 'root' && (settings().windowed?.[field.key] !== undefined || settings().maximized?.[field.key] !== undefined)}>
                  <span class="v2-settings-field__help" style="color: var(--gold);">
                    Per-view overrides —
                    <Show when={settings().windowed?.[field.key] !== undefined}> windowed: <b>{String(settings().windowed[field.key])}</b></Show>
                    <Show when={settings().maximized?.[field.key] !== undefined}> · maximized: <b>{String(settings().maximized[field.key])}</b></Show>
                  </span>
                </Show>
              </label>
            );
          }}</For>
        </div>
        <footer>
          <button class="v2-btn-sm" onClick={reset} {...tipProps({ content: "Reset all fields and layers to defaults", pin: false })}>Reset all</button>
          <span class="v2-settings-hint">Saved automatically.</span>
        </footer>
      </Show>
    </div>
  );
}

function Field(p) {
  const { field } = p;
  if (field.type === 'select') {
    return (
      <select class="v2-input" value={p.value ?? ''} disabled={p.disabled}
        onChange={(e) => {
          const raw = e.currentTarget.value;
          const opt = field.options.find(o => String(o.value) === raw);
          p.onInput(opt ? opt.value : raw);
        }}>
        <For each={field.options}>{(o) => (
          <option value={o.value}>{o.label}</option>
        )}</For>
      </select>
    );
  }
  if (field.type === 'number') {
    return (
      <input class="v2-input" type="number"
        value={p.value ?? ''}
        disabled={p.disabled}
        min={field.min} max={field.max} step={field.step || 1}
        placeholder={field.placeholder || (p.effective != null ? `default: ${p.effective}` : '')}
        onInput={(e) => {
          const v = e.currentTarget.value;
          p.onInput(v === '' ? undefined : Number(v));
        }} />
    );
  }
  if (field.type === 'toggle') {
    // Issue #101 — fall back to the schema-declared default when no
    // explicit user value has been saved yet. Without this the checkbox
    // renders unchecked even though the widget honors a `default: true`
    // semantic, so toggling it to true looks like a no-op and toggling
    // to false silently writes a layer override the user did not intend.
    const checked = p.value === undefined ? !!field.default : !!p.value;
    return (
      <input type="checkbox" checked={checked}
        disabled={p.disabled}
        onChange={(e) => p.onInput(e.currentTarget.checked)} />
    );
  }
  if (field.type === 'date') {
    // Stored as UNIX seconds; rendered as YYYY-MM-DD. Empty input clears
    // the layer so the schema/effective default applies.
    const toIso = (sec) => {
      if (!Number.isFinite(+sec) || +sec <= 0) return '';
      try { return new Date(+sec * 1000).toISOString().slice(0, 10); } catch { return ''; }
    };
    return (
      <input class="v2-input" type="date"
        value={toIso(p.value)}
        disabled={p.disabled}
        onInput={(e) => {
          const v = e.currentTarget.value;
          if (!v) return p.onInput(undefined);
          // Treat as start-of-day local time → UNIX seconds.
          const ms = new Date(v + 'T00:00:00').getTime();
          p.onInput(Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined);
        }} />
    );
  }
  if (field.type === 'multi-toggle') {
    const selected = () => Array.isArray(p.value) ? p.value : [];
    function toggle(v, on) {
      const cur = new Set(selected());
      if (on) cur.add(v); else cur.delete(v);
      p.onInput(Array.from(cur));
    }
    return (
      <div class="v2-settings-multi">
        <For each={field.options}>{(o) => (
          <label class="v2-settings-multi__opt">
            <input type="checkbox"
              disabled={p.disabled}
              checked={selected().includes(o.value)}
              onChange={(e) => toggle(o.value, e.currentTarget.checked)} />
            <span>{o.label}</span>
          </label>
        )}</For>
      </div>
    );
  }
  // text (default)
  return (
    <input class="v2-input" type="text"
      value={p.value ?? ''}
      disabled={p.disabled}
      placeholder={field.placeholder || (p.effective != null ? `default: ${p.effective}` : '')}
      onInput={(e) => p.onInput(e.currentTarget.value || undefined)} />
  );
}
